import type { ProgressSectionData } from '../hooks/usePlayerStatsTab'
import { MISSING_STAT } from '../constants'
import type {
  ChartPoint,
  ProgressRange,
  ProgressComparison,
  ProgressTile,
  StatChart,
  StatDelta,
} from '../types'
import { buildGamesDelta, buildStatDelta } from './statDelta'
import { getStatName } from './statNames'
import { formatStatValue } from './statValues'

export interface BuildProgressTilesArgs {
  progress: ProgressSectionData
  /** First charted stat's abbr; `null` when no stat is charted (All Sports). */
  abbr: string | null
  range: ProgressRange
  isAllSports: boolean
  /** Abbr → full stat name; the stat tile is labelled with the full name. */
  statNames: Record<string, string>
}

export const sumGamesPlayed = (points: ChartPoint[]): number =>
  points.reduce((sum, p) => sum + p.gamesPlayed, 0)

/** The period with the most games (the first on a tie); null without games. */
const pickBusiest = (points: ChartPoint[]): ChartPoint | null =>
  points.reduce<ChartPoint | null>(
    (busiest, point) =>
      point.gamesPlayed > (busiest?.gamesPlayed ?? 0) ? point : busiest,
    null
  )

/** What a range total covers: "all time" or "in range". */
export const getRangeCaption = (range: ProgressRange): string =>
  range === 'all' ? 'all time' : 'in range'

/**
 * The stat tile's caption: only 'all time' — over any other range the
 * comparison line under the value already says what the total covers.
 */
const rangeCaption = (range: ProgressRange): string =>
  range === 'all' ? getRangeCaption(range) : ''

/** Several stats charted: the stat and best-period tiles list one line each. */
const buildMultiStatTiles = (
  progress: ProgressSectionData,
  range: ProgressRange,
  statNames: Record<string, string>
): ProgressTile[] => {
  const { charts, comparison } = progress
  return [
    {
      label: 'Selected Stats',
      value: '',
      caption: rangeCaption(range),
      lines: charts.map(({ abbr, colorSlot, chart }) => ({
        colorSlot,
        label: getStatName(statNames, abbr),
        value: formatStatValue(chart.aggregate, abbr),
        caption: '',
        delta: buildStatDelta(comparison, abbr),
      })),
    },
    {
      label: 'Games Played',
      // Every charted stat reads the same periods, so any chart's total works.
      value: String(charts[0]?.chart.totalGames ?? 0),
      caption: '',
      delta: buildGamesDelta(comparison),
    },
    {
      label: 'Best Period',
      value: '',
      caption: '',
      lines: charts.map(({ abbr, colorSlot, chart }) => ({
        colorSlot,
        label: getStatName(statNames, abbr),
        value: formatStatValue(chart.best?.value ?? null, abbr),
        caption: chart.best?.label ?? 'No games',
      })),
    },
  ]
}

const buildSportTiles = (
  progress: ProgressSectionData,
  abbr: string,
  range: ProgressRange,
  statNames: Record<string, string>
): ProgressTile[] => {
  if (progress.charts.length > 1) {
    return buildMultiStatTiles(progress, range, statNames)
  }
  const chart = progress.charts[0]?.chart
  const best = chart?.best ?? null
  return [
    {
      label: getStatName(statNames, abbr),
      value: formatStatValue(chart?.aggregate ?? null, abbr),
      caption: rangeCaption(range),
      delta: buildStatDelta(progress.comparison, abbr),
    },
    {
      label: 'Games Played',
      value: String(chart?.totalGames ?? 0),
      caption: '',
      delta: buildGamesDelta(progress.comparison),
    },
    {
      label: 'Best Period',
      value: formatStatValue(best?.value ?? null, abbr),
      caption: best?.label ?? 'No games',
    },
  ]
}

const buildAllSportsTiles = (progress: ProgressSectionData): ProgressTile[] => {
  const busiest = pickBusiest(progress.gamesChart)
  return [
    {
      label: 'Games Played',
      value: String(sumGamesPlayed(progress.gamesChart)),
      caption: '',
      delta: buildGamesDelta(progress.comparison),
    },
    {
      label: 'Busiest Period',
      value: busiest ? String(busiest.gamesPlayed) : MISSING_STAT,
      caption: busiest?.label ?? 'No games',
    },
  ]
}

/**
 * Summary tiles for the Progress card. A sport gets three: the stat over the
 * range, Games Played, Best Period. All Sports gets Games Played and Busiest
 * Period. The current week lives in the This Week strip.
 */
export const buildProgressTiles = ({
  progress,
  abbr,
  range,
  isAllSports,
  statNames,
}: BuildProgressTilesArgs): ProgressTile[] =>
  isAllSports || abbr === null
    ? buildAllSportsTiles(progress)
    : buildSportTiles(progress, abbr, range, statNames)

/** One charted stat in a compact multi-stat summary (a row, not a tile). */
export interface StatSummaryRow {
  abbr: string
  colorSlot: number
  name: string
  /** The range total (or rebuilt rate). */
  total: string
  /** Best period's value; `–` without games. */
  best: string
  /** Best period's label, e.g. "Aug '23"; "No games" without one. */
  bestPeriod: string
  /** The total against the previous window; null without a change. */
  delta: StatDelta | null
}

/**
 * The charted stats as rows — total and best period side by side — for a
 * narrow screen where per-stat tiles would truncate the names.
 */
export const buildStatSummaryRows = (
  charts: StatChart[],
  statNames: Record<string, string>,
  comparison: ProgressComparison | null
): StatSummaryRow[] =>
  charts.map(({ abbr, colorSlot, chart }) => ({
    abbr,
    colorSlot,
    name: getStatName(statNames, abbr),
    total: formatStatValue(chart.aggregate, abbr),
    best: formatStatValue(chart.best?.value ?? null, abbr),
    bestPeriod: chart.best?.label ?? 'No games',
    delta: buildStatDelta(comparison, abbr),
  }))
